import { useMemo } from 'react';
import { camelCase, compact, uniqueId } from 'lodash';

import IconChrome from 'src/assets/icons/IconChrome';
import {
  BrandVoiceIcon,
  IntegrationsResourcesIcon,
  Performance,
  SavedTemplatesIcon,
} from 'src/assets/icons/navbar-links';
import { EN_LANGUAGES } from 'src/data/generation';
import { INFINITY_EXTENSION_STORE_URL } from 'src/data/infinity';
import { STONLY_ID } from 'src/lib/stonly';
import { ROUTES } from 'src/routes';
import { useBrandVoices } from 'src/services';
import { useCustomerPerformanceChartEnabled } from 'src/services/customer';
import { ANALYTICS_PAGE_VIEW } from 'src/views/CopyAnalyticsPage';

import { ITEM_TYPE } from '../lib';

const createLinkItem = ({
  label,
  path,
  icon,
  isChildItem,
  popupKey,
  isEmptyItem,
  dataStonlyId,
  externalLink,
}) => ({
  type: ITEM_TYPE.link,
  id: `${camelCase(label)}LinkItem`,
  label,
  path,
  icon,
  popupKey,
  isEmptyItem,
  isChildItem,
  dataStonlyId,
  externalLink,
});

const createHeaderItem = ({ label }) => ({
  type: ITEM_TYPE.header,
  id: uniqueId('header'),
  label,
});

const createMenuItem = ({
  label,
  icon,
  items,
  displayCreateButton,
  isChildItem,
  dataStonlyId,
}) => ({
  type: ITEM_TYPE.menu,
  id: `${camelCase(label)}MenuItem`,
  label,
  icon,
  items,
  isChildItem,
  displayCreateButton,
  dataStonlyId,
});

const createBrandVoiceMenuItem = ({ id, label, items, displayActionsMenu, language }) => {
  return {
    id: `${camelCase(label)}${id}CollapsableItem`,
    label,
    type: ITEM_TYPE.collapse,
    items,
    displayActionsMenu,
    brandVoice: {
      id,
      name: label,
      language,
    },
  };
};

const createEmptyItem = () =>
  createLinkItem({ label: 'No brand voices are accessible', isEmptyItem: true });

export const useNavigationSidebarItems = () => {
  const { brandVoices } = useBrandVoices();
  const performanceChartEnabled = useCustomerPerformanceChartEnabled();

  return useMemo(
    () => [
      createHeaderItem({ label: 'Assets & Tools' }),

      createMenuItem({
        label: 'Content Intelligence',
        icon: Performance,
        items: compact([
          createLinkItem({
            label: performanceChartEnabled ? 'Data rows' : 'Analytics',
            path: ROUTES.ANALYTICS,
            isChildItem: true,
          }),
          performanceChartEnabled &&
            createLinkItem({
              label: 'Analytics',
              path: `${ROUTES.ANALYTICS}/${ANALYTICS_PAGE_VIEW.chart}`,
              isChildItem: true,
            }),
          createLinkItem({
            label: 'Talking Points',
            path: ROUTES.TALKING_POINTS,
            isChildItem: true,
          }),
        ]),
      }),

      createMenuItem({
        label: 'Brand Voice',
        displayCreateButton: true,
        icon: BrandVoiceIcon,
        dataStonlyId: STONLY_ID.brandVoicesSidebarItem,
        items:
          brandVoices?.length > 0
            ? brandVoices.map(({ id, name, language }) => {
                const isEnglishOrUndefined = EN_LANGUAGES.includes(language) || !language;

                const items = [
                  createLinkItem({
                    label: 'Tone of Voice',
                    path: `${ROUTES.BRAND_VOICE}/${id}${ROUTES.TONE_OF_VOICE}`,
                    isChildItem: true,
                  }),
                  createLinkItem({
                    label: 'Target Audiences',
                    path: `${ROUTES.BRAND_VOICE}/${id}${ROUTES.PERSONAS}`,
                    isChildItem: true,
                  }),
                  createLinkItem({
                    label: 'Messaging Bank',
                    path: `${ROUTES.BRAND_VOICE}/${id}${ROUTES.BRAND_BRIEFS}`,
                    isChildItem: true,
                  }),
                  createLinkItem({
                    label: 'Brand Vocabulary',
                    path: `${ROUTES.BRAND_VOICE}/${id}${ROUTES.BRAND_TERMS}`,
                    isChildItem: true,
                  }),
                ];

                if (isEnglishOrUndefined) {
                  items.unshift(
                    createLinkItem({
                      label: 'Custom Models',
                      path: `${ROUTES.BRAND_VOICE}/${id}${ROUTES.CUSTOM_MODELS}`,
                      isChildItem: true,
                    })
                  );
                  items.push(
                    createLinkItem({
                      label: 'Formulas',
                      path: `${ROUTES.BRAND_VOICE}/${id}${ROUTES.CUSTOM_FORMULA}`,
                      isChildItem: true,
                    })
                  );
                }

                return createBrandVoiceMenuItem({
                  id,
                  label: name,
                  language,
                  displayActionsMenu: true,
                  items,
                });
              })
            : [createEmptyItem()],
      }),

      createLinkItem({
        label: 'Template Builder',
        path: ROUTES.TEMPLATE_BUILDER,
        icon: SavedTemplatesIcon,
        dataStonlyId: STONLY_ID.templateBuilderSidebarItem,
      }),

      createLinkItem({
        label: 'Integrations & Resources',
        path: ROUTES.INTEGRATIONS_RESOURCES,
        icon: IntegrationsResourcesIcon,
      }),

      createLinkItem({
        label: 'Website Automation',
        path: ROUTES.WEBSITE_AUTOMATION,
        icon: SavedTemplatesIcon,
      }),

      createLinkItem({
        label: 'Get Chrome Extension',
        path: INFINITY_EXTENSION_STORE_URL,
        icon: IconChrome,
        externalLink: true,
      }),
    ],
    [brandVoices, performanceChartEnabled]
  );
};
